import { t } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { isAndroid } from 'react-device-detect';
import { ArrowLeft, Attention, Share } from '@peacecover/icon';
import { useGetInvitationRecap } from '../../domains/user/ui/hooks/useGetInvitationRecap';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import copyTextToClipboard from '../../utils/writeTextToClipboard';
import { ReactComponent as LikeGradientShape } from '../../icons/likeGradientShape.svg';

const InviteFriendsPage = () => {
  const { data: bettieBalances } = useGetBettieBalances();
  const { data: invitationRecap } = useGetInvitationRecap();
  const handleHistoryBack = useHandleHistoryBack();
  const showSuccessToast = useSuccessPhotoCardToast();

  const handleCopyInvitationCode = () => {
    const downloadLink = isAndroid
      ? 'https://play.google.com/store/apps/details?id=com.peacecover.pocapan&hl=ko'
      : 'https://apps.apple.com/kr/app/pocapan-%ED%8F%AC%EC%B9%B4%ED%8C%90/id6449088562';
    const message = `FanHearts: 덕질을 편하게!
💗 팬하트에 가입하면 하트 무료 지급
이 하트로 최애를 닮은 대화상대와 채팅까지 가능해요!

🎁 하트 받는 방법
① 팬하트 앱 다운로드 📲
② 회원가입 후
  팬하트 > 마이페이지 > 무료로 하트 얻기 > 코드 입력하기
③ 아래 코드 입력하면 하트 지급!

🔑 코드: ${invitationRecap?.invitationCode}
⬇️ 다운로드 링크: ${downloadLink}`;
    copyTextToClipboard(message).then(() => {
      showSuccessToast('bottom', t('InviteFriends.copyMessageToClipboard') as string);
    });
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-light dark:bg-background-dark">
      <header className="px-[16px] py-[10px] flex items-center justify-between">
        <ArrowLeft size={24} className="text-text-main-lv1" onClick={handleHistoryBack} />
        <div className="flex items-center gap-[2px]">
          <LikeGradientShape width={20} height={20} />
          <p className="text-[16px] font-medium leading-[19px] text-text-main-lv1">
            {t('InviteFriends.heartCount', { count: bettieBalances?.bettiePoint?.balance ?? 0 })}
          </p>
        </div>
      </header>
      <EventBanner />
      <div className="flex flex-col items-center px-[24px]">
        <div className="mt-[24px] flex flex-col gap-[16px] items-center">
          <img
            src="/assets/icons/graphic-3d/3D.like.png"
            alt="like"
            className="w-[48px] h-[48px]"
          />
          <h1 className="text-[17px] font-bold leading-[20px] text-text-main-lv2">
            {t('InviteFriends.title')}
          </h1>
        </div>
        <div className="w-full bg-separator1 rounded-[20px] px-[15px] py-[20px] mt-[38px] flex flex-col items-center">
          <div className="flex flex-col items-center py-[10px] gap-[14px]">
            <p className="text-[18px] font-semibold leading-[21px] text-text-main-lv2">
              {t('InviteFriends.myRedeemCode')}
            </p>
            <p className="text-[14px] font-medium leading-[17px] text-text-main-lv4 text-center whitespace-pre-line">
              <Trans
                i18nKey="InviteFriends.description"
                components={{
                  bold: <span className="font-bold" />
                }}
              />
            </p>
          </div>
          <div className="mt-[20px] bg-background-light dark:bg-background-dark rounded-[14px] py-[19px] w-full flex items-center justify-center shadow-[0_0_8px_0_rgba(0,0,0,0.02)_inset,_0_-1px_4px_0_rgba(255,255,255,0.78)_inset,_0_1px_4px_0_rgba(85,11,11,0.11)_inset] dark:shadow-[0_0_8px_0_rgba(0,0,0,0.02)_inset,_0_-1px_4px_0_rgba(255,255,255,0.41)_inset,_0_1px_4px_0_rgba(85,11,11,0.11)_inset]">
            <p className="text-[17px] font-medium leading-[20px] text-text-main-lv2 text-center">
              {invitationRecap?.invitationCode}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCopyInvitationCode}
            className="flex items-center justify-center gap-[4px] bg-gradient-to-r from-[#FF45A9] to-[#FF6A6A] rounded-full py-[14px] w-full mt-[29px]"
          >
            <Share size={24} className="text-pure-white" />
            <p className="text-[17px] font-semibold leading-[20px] text-pure-white">
              {t('InviteFriends.shareFriends')}
            </p>
          </button>
        </div>
        <p className="text-[14px] font-semibold leading-[17px] text-center text-text-main-lv3 mt-[24px]">
          {t('InviteFriends.myHeartsWithCode', { count: invitationRecap?.earnedPoints ?? 0 })}
        </p>
      </div>
      <div className="fixed left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+28px)] flex items-center justify-center gap-[4px]">
        <Attention size={16} className="text-text-main-lv4" />
        <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3 whitespace-nowrap">
          {t('InviteFriends.onlyOneCode')}
        </p>
      </div>
    </div>
  );
};

export default InviteFriendsPage;

const EventBanner = () => {
  const { i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  return (
    <div className="mt-[7px] flex items-center justify-start pr-[18px] mx-[16px] bg-background-elevated rounded-[20px] shadow-[0_0_4px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.08)] dark:shadow-[0_0_4px_0_rgba(0, 0, 0, 0.36), 0_0_15px_0_rgba(0, 0, 0, 0.49)]">
      <img
        src={
          isEnLocale
            ? '/assets/freeCharge/invite_friends_event_en.png'
            : '/assets/freeCharge/invite_friends_event.png'
        }
        alt="event_banner"
        className="w-[105px] h-[69px]"
      />
      <p className="text-[14px] leading-[17px] text-text-main-lv2 font-semibold whitespace-pre-line">
        <Trans
          i18nKey="InviteFriends.eventTitle"
          components={{
            bold: <span className="bg-gradient-single-red text-transparent bg-clip-text" />
          }}
        />
      </p>
    </div>
  );
};
